/**
 * useRecordCRUD — generic per-agent record CRUD primitive for v2
 * workflow steps that surface a flat record list (Steps 2-8).
 *
 * Each call wraps one of the workflow API endpoints
 * (`addRecord` / `updateSingleRecord` / `deleteRecord`) with shared
 * loading + error state. The caller owns the data store (typically
 * `useStructuredFetch` for the list) and re-fetches after a
 * successful mutation.
 *
 * The hook is agent-agnostic: pass `agentName` (e.g.
 * `'asset_identification_agent'`) once at construction; CRUD methods
 * accept `record` / `recordId` / `patch`.
 *
 * Cascade-delete handling:
 *   The backend's delete endpoint returns 409 with
 *   `code === 'HAS_DEPENDENCIES'` when the record has downstream
 *   refs. `api/workflow.deleteRecord` already reshapes that into a
 *   typed Error. This hook lets the rejection propagate so callers
 *   can `catch` it and open a cascade-delete dialog, then retry
 *   with `remove(id, { cascade: true })`.
 *
 * `enabled`: gate set by the caller. Mutation methods reject when
 * the step status doesn't permit edits (typically `status ∈ {draft,
 * revising}`). The hook never reaches into reducer state itself.
 */
import { useCallback, useState } from 'react';
import {
  addRecord as addRecordApi,
  updateSingleRecord,
  deleteRecord as deleteRecordApi,
} from '../../../api/workflow';
import { notify } from '../../../services/notificationBus';

/** Human label for the steps a cascade can touch. */
const AGENT_LABEL: Record<string, string> = {
  damage_scenario_agent: 'damage scenario',
  attack_path_feasibility_agent: 'attack path',
  risk_assessment_agent: 'risk score',
  cybersecurity_goals_agent: 'CAL level',
};

/**
 * Tell the user when an edit re-derived ratings in later steps.
 *
 * The backend cascades deterministically and persists the result, but the
 * affected records live in steps the user isn't looking at — without this the
 * recalculation is invisible and reads as data changing on its own.
 */
export function announceRecalculation(response: unknown): void {
  const res = response as
    | {
        recalculation_summary?: { records_changed?: number; by_agent?: Record<string, number> };
        stale_narrative?: unknown[];
      }
    | undefined;
  const changed = res?.recalculation_summary?.records_changed ?? 0;
  if (!changed) return;

  const parts = Object.entries(res?.recalculation_summary?.by_agent ?? {}).map(([agent, n]) => {
    const label = AGENT_LABEL[agent] ?? agent.replace(/_agent$/, '').replace(/_/g, ' ');
    return `${n} ${label}${n === 1 ? '' : 's'}`;
  });
  const detail = parts.length ? ` (${parts.join(', ')})` : '';
  const staleCount = Array.isArray(res?.stale_narrative) ? res!.stale_narrative!.length : 0;
  const staleNote = staleCount
    ? ` ${staleCount} rationale${staleCount === 1 ? '' : 's'} may now be out of date.`
    : '';

  notify({
    type: 'info',
    message: `Recalculated ${changed} dependent rating${changed === 1 ? '' : 's'}${detail}.${staleNote}`,
  });
}

export interface UseRecordCRUDInput {
  threadId: string | null | undefined;
  agentName: string;
  /** Set to false when the step status is read-only — methods then reject. */
  enabled: boolean;
}

export interface DeleteOptions {
  cascade?: boolean;
  force?: boolean;
}

export interface UseRecordCRUDState {
  saving: boolean;
  error: string | null;
  add: (record: Record<string, unknown>) => Promise<unknown>;
  update: (recordId: string, patch: Record<string, unknown>) => Promise<unknown>;
  remove: (recordId: string, opts?: DeleteOptions) => Promise<unknown>;
  clearError: () => void;
}

const NOT_READY = new Error(
  'This step is not in an editable state. Approve or revise before making changes.',
);

const NO_THREAD = new Error(
  'No active workflow session. Start analysis before editing records.',
);

export function useRecordCRUD({
  threadId,
  agentName,
  enabled,
}: UseRecordCRUDInput): UseRecordCRUDState {
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const guard = useCallback((): void => {
    if (!threadId) throw NO_THREAD;
    if (!enabled) throw NOT_READY;
  }, [threadId, enabled]);

  const run = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      setSaving(true);
      setError(null);
      try {
        return await fn();
      } catch (e: any) {
        // Preserve cascade-delete shape — caller switches on
        // `code === 'HAS_DEPENDENCIES'` to open the dialog.
        const msg = e?.response?.data?.detail || e?.message || 'Request failed';
        setError(typeof msg === 'string' ? msg : 'Request failed');
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const add = useCallback(
    (record: Record<string, unknown>) => {
      guard();
      return run(() => addRecordApi(threadId as string, agentName, record));
    },
    [guard, run, threadId, agentName],
  );

  const update = useCallback(
    async (recordId: string, patch: Record<string, unknown>) => {
      guard();
      const res = await run(() =>
        updateSingleRecord(threadId as string, agentName, recordId, patch),
      );
      // Editing a rating re-derives everything downstream of it. Those records
      // live in later steps, which refetch their own data — so the change is
      // silent unless we say it happened.
      announceRecalculation(res);
      return res;
    },
    [guard, run, threadId, agentName],
  );

  const remove = useCallback(
    (recordId: string, opts: DeleteOptions = {}) => {
      guard();
      return run(() => deleteRecordApi(threadId as string, agentName, recordId, opts));
    },
    [guard, run, threadId, agentName],
  );

  const clearError = useCallback(() => setError(null), []);

  return { saving, error, add, update, remove, clearError };
}
